import React, { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { GET_BADGE_RELATED_WINES } from "../../../graphql/resolvers/badge";
import { BadgeRelatedWines as BadgeRelatedWinesType } from "../../../types/__generated__/BadgeRelatedWines";
import { ChallengeBadges_memberBadgesByCategory as Badge } from "../../../types/__generated__/ChallengeBadges";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { MemberRewardBasicInfo } from "../../../types/__generated__/MemberRewardBasicInfo";
import { AllWinesByWineListOrderField } from "../../../types/__generated__/globalTypes";
import {
  ChildChallengeBadges_memberChildBadgesFromChallengeBadge_badgerulesSet as BadgeRule,
  ChildChallengeBadges_memberChildBadgesFromChallengeBadge as ChildChallengeBadge,
} from "../../../types/__generated__/ChildChallengeBadges";
import {
  BADGE_CATEGORY_IDS,
  I18N_NAMESPACES,
  WINE_FILTERS as FILTERS,
  BADGE_RULE_CONTENT_TYPE_MODELS,
} from "../../../helpers/constants";
import urlPatterns from "../../../urls";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import ChallengeWineCard from "../ChallengeWineCard";
import BadgeRelatedWinesHeader from "../BadgeRelatedWinesHeader";
import explorerImage from "../../../assets/images/general/explorer_bw.png";
import { useWineDetailsContext } from "../../organisms/WineDetailsTray/context";
import {
  OuterContainer,
  ChallengeTitle,
  WineCardsContainer,
  ChallengeWinesGradientContainer,
  ChallengesWinesRelativeContainer,
  StyledButtonContainer,
  StyledButton,
  StyledExplorerImage,
  PlaceholderText,
  PlaceholderContainer,
} from "./style";

interface FiltersToObjectIds {
  wineRegionIds: number[];
  countryIds: number[];
  wineProductionMethodIds: number[];
  wineTypeIds: number[];
  wineClassIds: number[];
  wineBodyId: number | null;
}

interface BadgeRelatedWinesProps {
  shouldConcealWines: boolean;
  memberShoppingCartQuery: MemberShoppingCart | MemberRewardBasicInfo | null | undefined;
  badge: Badge | null | undefined;
  onWineDetailsClick: Function;
  onTopUpClick: Function;
  memberChildBadgesFromChallengeBadge: (ChildChallengeBadge | null)[] | null | undefined;
  isBadgeEarned?: boolean;
}

const BadgeRelatedWines = ({
  shouldConcealWines,
  memberShoppingCartQuery,
  badge,
  onWineDetailsClick,
  onTopUpClick,
  memberChildBadgesFromChallengeBadge,
  isBadgeEarned = false,
}: BadgeRelatedWinesProps) => {
  const history = useHistory();
  const { closeWineDetailsTray } = useWineDetailsContext();
  const { t } = useTranslation([I18N_NAMESPACES.MY_BADGES, I18N_NAMESPACES.COMMON]);
  const initialIsRecommendedSelected = !isBadgeEarned;
  const [isRecommendedSelected, setIsRecommendedSelected] = useState<boolean>(initialIsRecommendedSelected);

  const isChallengeBadge = badge?.badgeCategory?.id === BADGE_CATEGORY_IDS.DB_ID_BADGE_CATEGORY_CHALLENGES;

  const { data: badgeWinesData, loading: isBadgeWinesLoading } = useQuery<BadgeRelatedWinesType>(
    GET_BADGE_RELATED_WINES,
    {
      variables: {
        badgeId: badge?.id,
      },
    }
  );

  const hasWineToRecommend = !!badgeWinesData?.badgeRelatedWines?.badgeRecommendedWines?.length;

  /**
   * Combines two FiltersToObjectIds types, while removing duplicates in each section.
   */
  const combineBadgeFilters = (
    filtersA: FiltersToObjectIds,
    filtersB: FiltersToObjectIds
  ): FiltersToObjectIds => ({
    countryIds: Array.from(new Set([...filtersA.countryIds, ...filtersB.countryIds])),
    wineRegionIds: Array.from(new Set([...filtersA.wineRegionIds, ...filtersB.wineRegionIds])),
    wineTypeIds: Array.from(new Set([...filtersA.wineTypeIds, ...filtersB.wineTypeIds])),
    wineBodyId: filtersA.wineBodyId ?? filtersB.wineBodyId,
    wineClassIds: Array.from(new Set([...filtersA.wineClassIds, ...filtersB.wineClassIds])),
    wineProductionMethodIds: Array.from(
      new Set([...filtersA.wineProductionMethodIds, ...filtersB.wineProductionMethodIds])
    ),
  });

  /**
   * Gets the first badge rule related model from the badge.
   */
  const getFirstBadgeRuleModel = (): string | undefined => {
    const models = badge?.badgerulesSet && badge?.badgerulesSet.map((br) => br?.contentType?.model);
    return models?.[0];
  };

  /**
   * Gets the translated name for the contentModel (the object on a badge rule).
   */
  const getContentTypeCopy = (contentModel: string | undefined): string => {
    switch (contentModel) {
      case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_CLASS:
        return t("myBadges:contentTypeModelsToCopy.wineclass");
      case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_TYPE:
        return t("myBadges:contentTypeModelsToCopy.winetype");
      case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_BODY:
        return t("myBadges:contentTypeModelsToCopy.winebody");
      case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_PROD_METHOD:
        return t("myBadges:contentTypeModelsToCopy.wineproductionmethod");
      case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_REGION:
        return t("myBadges:contentTypeModelsToCopy.wineregion");
      case BADGE_RULE_CONTENT_TYPE_MODELS.COUNTRY:
        return t("myBadges:contentTypeModelsToCopy.country");
      case BADGE_RULE_CONTENT_TYPE_MODELS.BADGE:
        return t("myBadges:contentTypeModelsToCopy.badge");
      default:
        return t("myBadges:contentTypeModelsToCopy.default");
    }
  };

  /**
   * Gets the content object ids from all of the badge rules.
   */
  const getContentObjectIdsFromBadgeRules = (badgeRulesSet: BadgeRule[]): FiltersToObjectIds => {
    const filtersToObjectIds: FiltersToObjectIds = {
      wineTypeIds: [],
      wineRegionIds: [],
      wineProductionMethodIds: [],
      countryIds: [],
      wineBodyId: null,
      wineClassIds: [],
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    badgeRulesSet?.forEach((badgeRule: any) => {
      const badgeObjectId = badgeRule?.objectId;
      const contentTypeModel = badgeRule?.contentType?.model;

      switch (contentTypeModel) {
        case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_REGION:
          badgeObjectId && filtersToObjectIds.wineRegionIds.push(badgeObjectId);
          break;
        case BADGE_RULE_CONTENT_TYPE_MODELS.COUNTRY:
          badgeObjectId && filtersToObjectIds.countryIds.push(badgeObjectId);
          break;
        case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_PROD_METHOD:
          badgeObjectId && filtersToObjectIds.wineProductionMethodIds.push(badgeObjectId);
          break;
        case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_TYPE:
          badgeObjectId && filtersToObjectIds.wineTypeIds.push(badgeObjectId);
          break;
        case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_CLASS:
          badgeObjectId && filtersToObjectIds.wineClassIds.push(badgeObjectId);
          break;
        case BADGE_RULE_CONTENT_TYPE_MODELS.WINE_BODY:
          filtersToObjectIds.wineBodyId = badgeObjectId;
          break;
        default:
          break;
      }
    });

    return filtersToObjectIds;
  };

  /*
   * Gets the content object ids from a badge based on the badge rules and their related models.
   */
  const getContentObjectIdsFromBadge = (
    badgeItem: Badge | ChildChallengeBadge | null | undefined
  ): FiltersToObjectIds => {
    const filtersToObjectIds: FiltersToObjectIds = {
      wineTypeIds: [],
      wineRegionIds: [],
      wineProductionMethodIds: [],
      countryIds: [],
      wineBodyId: null,
      wineClassIds: [],
    };

    const badgeRules = badgeItem?.badgerulesSet;

    if (badgeRules) {
      const badgeRulesFilters = getContentObjectIdsFromBadgeRules(badgeRules);
      return combineBadgeFilters(filtersToObjectIds, badgeRulesFilters);
    }

    return filtersToObjectIds;
  };

  /**
   * Returns the object filter to filter wines based on the badge, or child badges for challenges.
   * */
  const getWineFilters = (): object => {
    const numRecommendedWines = badgeWinesData?.badgeRelatedWines?.badgeRecommendedWines?.length || 0;

    // Don't filter wines (If there are no recommendations here, we have no stock anyway).
    if (numRecommendedWines === 0) {
      return {};
    }

    let filtersToObjectIds: FiltersToObjectIds = {
      wineTypeIds: [],
      wineRegionIds: [],
      wineProductionMethodIds: [],
      wineBodyId: null,
      wineClassIds: [],
      countryIds: [],
    };

    if (isChallengeBadge) {
      memberChildBadgesFromChallengeBadge?.forEach((childBadge) => {
        const childBadgeFilters = getContentObjectIdsFromBadge(childBadge);
        filtersToObjectIds = combineBadgeFilters(filtersToObjectIds, childBadgeFilters);
      });
    } else {
      filtersToObjectIds = getContentObjectIdsFromBadge(badge);
    }

    // The order by filter is always set to highest member likelihood.
    const selectedFilters = {
      [FILTERS.REGION]: filtersToObjectIds.wineRegionIds,
      [FILTERS.COUNTRY]: filtersToObjectIds.countryIds,
      [FILTERS.OTHER]: filtersToObjectIds.wineProductionMethodIds,
      [FILTERS.GRAPE_TYPE]: filtersToObjectIds.wineTypeIds,
      [FILTERS.BODY]: filtersToObjectIds.wineBodyId,
      [FILTERS.COLOUR]: filtersToObjectIds.wineClassIds,
      [FILTERS.ORDER_BY]: AllWinesByWineListOrderField.MEMBER_LIKELIHOOD_DESC,
    };

    return selectedFilters;
  };

  /**
   * Renders the wine cards for recommended wines.
   * */
  const renderRecommendedWineCards = (): ReactElement => {
    const recommendedWinesArray = badgeWinesData?.badgeRelatedWines?.badgeRecommendedWines ?? [];
    return (
      <WineCardsContainer>
        {recommendedWinesArray &&
          recommendedWinesArray.map((recommendedWine, index) => (
            <ChallengeWineCard
              key={recommendedWine?.id ?? index}
              index={index}
              memberShoppingCartQuery={memberShoppingCartQuery}
              isRecommendedWine
              wineObject={recommendedWine}
              onWineDetailsClick={onWineDetailsClick}
              shouldHideAddButton={shouldConcealWines}
              isInChallenges={isChallengeBadge}
            />
          ))}
      </WineCardsContainer>
    );
  };

  /**
   * Renders the wine cards for past wines.
   * */
  const renderPastWineCards = (): ReactElement => {
    const pastWinesArray = badgeWinesData?.badgeRelatedWines?.badgeAlreadyDrunkWines ?? [];
    return (
      <WineCardsContainer>
        {pastWinesArray &&
          pastWinesArray.map((pastWine, index) => (
            <ChallengeWineCard
              key={pastWine?.id ?? index}
              index={index}
              memberShoppingCartQuery={memberShoppingCartQuery}
              isRecommendedWine={false}
              wineObject={pastWine}
              onWineDetailsClick={onWineDetailsClick}
              onTopUpClick={onTopUpClick}
            />
          ))}
      </WineCardsContainer>
    );
  };

  /**
   * Renders the button to see all past wines or all wines.
   * */
  const renderSeeAllWinesButton = (): ReactElement => {
    const buttonText = isRecommendedSelected ? t("common:seeAllWines") : t("common:seeAllPastWines");
    const path = isRecommendedSelected ? urlPatterns.ALL_WINES : urlPatterns.MY_CELLAR;
    const selectedFilters = getWineFilters();

    return (
      <StyledButtonContainer>
        <StyledButton
          onClick={() => {
            history.push({
              pathname: path,
              state: { selectedFilters },
            });
            closeWineDetailsTray && closeWineDetailsTray();
          }}
        >
          {buttonText}
        </StyledButton>
      </StyledButtonContainer>
    );
  };

  /**
   * Renders the placeholder image and text for when there is no recommended wine.
   * */
  const renderPlaceholder = (): ReactElement => (
    <>
      <PlaceholderContainer>
        <StyledExplorerImage src={explorerImage} />
        <PlaceholderText>
          {t("myBadges:badgeRecommendations.placeholder", {
            contentTypeModel: contentTypeModelName,
          })}
        </PlaceholderText>
      </PlaceholderContainer>
      {renderSeeAllWinesButton()}
    </>
  );

  /**
   * Renders the wine cards for recommended or past wines.
   * */
  const renderWineCards = (): ReactElement => {
    if (isBadgeWinesLoading) {
      return <StyledLoading />;
    }

    if (isRecommendedSelected && !hasWineToRecommend) {
      return renderPlaceholder();
    }

    return (
      <>
        <ChallengesWinesRelativeContainer>
          {isRecommendedSelected ? renderRecommendedWineCards() : renderPastWineCards()}
          {shouldConcealWines && <ChallengeWinesGradientContainer />}
        </ChallengesWinesRelativeContainer>
        {!shouldConcealWines && renderSeeAllWinesButton()}
      </>
    );
  };

  const getTitle = (): string => {
    if (isChallengeBadge && isBadgeEarned) {
      return t("myBadges:challengeRecommendations.titleEarned");
    }
    if (isChallengeBadge && !isBadgeEarned) {
      return t("myBadges:challengeRecommendations.title");
    }
    return t("myBadges:badgeRecommendations.title");
  };

  const contentModel = getFirstBadgeRuleModel();
  const contentTypeModelName = getContentTypeCopy(contentModel);

  return (
    <OuterContainer>
      <ChallengeTitle>{getTitle()}</ChallengeTitle>

      <BadgeRelatedWinesHeader
        setIsRecommendedSelected={setIsRecommendedSelected}
        shouldConcealWines={shouldConcealWines}
        badge={badge}
        isRecommendedSelected={isRecommendedSelected}
        badgeRelatedWines={badgeWinesData?.badgeRelatedWines}
        contentTypeModelName={contentTypeModelName}
        isBadgeEarned={isBadgeEarned}
        isBadgeWinesLoading={isBadgeWinesLoading}
      />
      {renderWineCards()}
    </OuterContainer>
  );
};

export default BadgeRelatedWines;
